import { Input, Select } from 'antd';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { useLocalStorageState } from 'ahooks';
import type { LoadMoreProps } from 'components/LoadMore';
import { MinusOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { useState } from 'react';

const { Search } = Input;
const { Option } = Select;

export type SearchParams = {
  // eslint-disable-next-line camelcase
  external_trade_ids?: string;
  itemName?: string;
  spuId?: string;
  sku: string;
  sellerSku: string;
};

type TradeSearchBarProps = {
  loading: boolean;
  value: Partial<SearchParams>;
  handleSearch: (params: SearchParams) => void;
};

function TradeSearchBar(props: TradeSearchBarProps) {
  const { loading, value, handleSearch } = props;
  const { t } = useTranslate();
  const [localSearchType, setLocalSearchType] = useLocalStorageState<string>(
    'trade_sider_search_type_value',
    {
      defaultValue: 'external_trade_ids',
    },
  );
  const [searchType, setSearchType] = useState<string>(
    () => Object.entries(value).find(([, value]) => value)?.[0] || localSearchType,
  );

  const onSearch = (value: any) => {
    setLocalSearchType(searchType);
    handleSearch(
      (value
        ? {
            [searchType!]: value,
          }
        : {
            external_trade_ids: undefined,
            itemName: undefined,
            spuId: undefined,
            sku: undefined,
            sellerSku: undefined,
          }) as SearchParams,
    );
  };

  const selectBefore = (
    <Select
      className="max-w-[150px]"
      dropdownMatchSelectWidth={false}
      value={searchType}
      onChange={(value) => setSearchType(value)}
    >
      <Option value="external_trade_ids">
        {t('scene.chatWorkspace.orderTab.search.type.orderId')}
      </Option>
      <Option value="trackingNumber">
        {t('scene.chatWorkspace.orderTab.search.type.trackingNumber')}
      </Option>
      <Option value="itemName">{t('scene.chatWorkspace.orderTab.search.type.itemName')}</Option>
      <Option value="spuId">{t('scene.chatWorkspace.orderTab.search.type.spuId')}</Option>
      <Option value="sku">{t('scene.chatWorkspace.orderTab.search.type.sku')}</Option>
      <Option value="sellerSku">{t('scene.chatWorkspace.orderTab.search.type.sellerSku')}</Option>
    </Select>
  );

  return (
    <Searchbar>
      <Search
        addonBefore={selectBefore}
        allowClear
        enterButton
        loading={loading}
        placeholder={t(`placeholder.${searchType}`)}
        defaultValue={Object.values(value).find((_) => _)}
        onSearch={onSearch}
      />
    </Searchbar>
  );
}

export const searchLoadRenderer: (tip: string) => LoadMoreProps['loadRenderer'] =
  (tip) => (nodes, hasMore, hasError) => {
    const { loadMore, loadError } = nodes;

    if (hasError) {
      return loadError;
    }

    if (hasMore) {
      return loadMore;
    }

    return (
      <Footer>
        <MinusOutlined className={tipColor} />
        <span className={tipColor}>{tip}</span>
        <MinusOutlined className={tipColor} />
      </Footer>
    );
  };

const tipColor = css`
  color: var(--fett-text-tertiary);
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 30px;
  > * {
    padding: 0 5px;
  }
`;

const Searchbar = styled.div`
  padding: 8px 12px;
  background: var(--fett-bg-panel);
  border: 0;
  border-bottom: 1px solid var(--fett-border-default);
  .ant-input-search-button {
    padding: 0 8px !important;
  }
  .ant-input-group-addon,
  .ant-select-selector,
  .ant-input {
    background: var(--fett-bg-input) !important;
    color: var(--fett-text-primary);
    border-color: var(--fett-border-strong) !important;
  }
  .ant-select-selection-item,
  .ant-select-arrow {
    color: var(--fett-text-primary);
  }
`;

export default TradeSearchBar;
